import { CssBaseline, type Theme } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import type { AppName } from '@pn/core/dependencies';
import { isDevelopment } from '@pn/core/utils/env';
import { webStorageService } from '@pn/services/storage/webStorageService';
import { isString } from 'lodash-es';
import React from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { themes } from './muiThemes';

const pnThemes = [
  'Stack Light',
  'Stack Dark',

  'BOE Light Legacy',
  'BOE Dark Legacy',

  'Hacker',
  'Holiday',
] as const;
export type PNTheme = (typeof pnThemes)[number];

export const productThemes: Record<AppName, PNTheme[]> = {
  maps: ['Stack Light', 'Stack Dark'],
  intel: ['Stack Light', 'Stack Dark', 'BOE Light Legacy', 'BOE Dark Legacy'],
  formations: ['Stack Light', 'Stack Dark'],
  forecast: ['Stack Light', 'Stack Dark'],
  admin: [...pnThemes],
};

type ContextType = {
  pnTheme: PNTheme;
  theme: Theme;
  setPNTheme: (pnTheme: PNTheme) => void;
};

const PNThemeContext = React.createContext({} as ContextType);

export const usePNThemeContext = () => React.useContext(PNThemeContext);

type Props = {
  children: React.ReactNode;
  defaultPNTheme: PNTheme;
  persist?: boolean;
};

export const PNThemeProvider = ({
  children,
  defaultPNTheme,
  persist = true,
}: Props) => {
  const [pnTheme, setPNTheme] = React.useState<PNTheme>(() => {
    if (!persist) return defaultPNTheme;
    try {
      const lsPNTheme = webStorageService('pn').get('theme');
      return isPNTheme(lsPNTheme) ? lsPNTheme : defaultPNTheme;
    } catch {
      return defaultPNTheme;
    }
  });

  const theme = usePNTheme(pnTheme);

  React.useEffect(() => {
    if (persist) webStorageService('pn').set('theme', pnTheme);
  }, [persist, pnTheme]);

  /**
   * Toggle between the light and dark themes.
   * Extra themes can only be enabled via the UI.
   */
  useHotkeys(
    'ctrl+alt+l',
    () => {
      const newPNTheme: PNTheme =
        theme.palette.mode === 'light'
          ? productThemes.maps[1]
          : productThemes.maps[0];
      setPNTheme(newPNTheme);
    },
    [theme.palette.mode]
  );

  const themeContextValue = React.useMemo(
    () => ({ pnTheme, theme, setPNTheme }),
    [pnTheme, theme]
  );

  return (
    <PNThemeContext.Provider value={themeContextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </PNThemeContext.Provider>
  );
};

function usePNTheme(pnTheme: PNTheme): Theme {
  return React.useMemo(() => {
    const theme = themes[pnTheme];
    if (isDevelopment()) {
      console.log(`%cMUI theme (${pnTheme})`, 'color: #607D8B', theme);
    }
    return theme;
  }, [pnTheme]);
}

export function isPNTheme(arg: unknown): arg is PNTheme {
  return isString(arg) && pnThemes.includes(arg as PNTheme);
}
